<script lang="ts">
	import { attrs } from '#library/attrs.ts'
	import type { Blok, Vertabs } from '#library/components.schema.build.ts'
	import Render from '#components/render.svelte'
	import Card from '#components/card.svelte'

	interface Props {
		blok: Blok<Vertabs>
	}

	let { blok }: Props = $props()
	let active = $state(0)
</script>

<section class="vertabs {blok.theme || 'theme-crocus-on-light-gimson'}" {...attrs(blok)}>
	<div class="contain">
		<div class="content">
			<div class="title">{blok.title?.text}</div>
			<div class="subtitle">{blok.subtitle?.text}</div>
			<div class="body">
				<Render content={blok.body} />
			</div>
			{#if blok.tabs?.length > 1}
				<div class="tabs">
					{#each blok.tabs as tab, i (i)}
						<button class="tab" class:active={active === i} onclick={() => (active = i)}>
							<div class="tab-title" class:active={active === i}>{tab.title}</div>
							<div class="tab-subtitle">{tab.subtitle}</div>
						</button>
					{/each}
				</div>
			{/if}
		</div>
		{#if blok.tabs?.length}
			<div>
				{#each blok.tabs as tab, i (i)}
					{#if active === i && tab.items?.length}
						<div class="cards">
							{#each tab.items as { suptitle, title, subtitle, body, image, theme, shape, link }, i (i)}
								<Card
									{suptitle}
									title={title!}
									{subtitle}
									{body}
									image={image!}
									{i}
									{theme}
									{shape}
									href={link?.url}
								/>
							{/each}
						</div>
					{/if}
				{/each}
			</div>
		{/if}
	</div>
</section>

<style lang="css">
	.vertabs {
		background-color: var(--c-background);
		color: var(--c-midground);
		padding-block-start: var(--padding-top, var(--padding-default));
		overflow: unset;

		&::before {
			@media (--from-mobile) {
				background-image: linear-gradient(
					to top,
					var(--c-background) 50%,
					var(--c-background) 50%,
					transparent
				);
				content: '';
				display: block;
				height: calc(var(--padding-bottom, var(--s-104)) * 2);
				margin-block-start: calc((var(--padding-bottom, var(--s-104)) * 2) * -1);
				pointer-events: none;
				position: sticky;
				top: calc(100vh - (var(--padding-bottom, var(--s-104)) * 2));
				width: 100%;
				z-index: 1;
			}
		}
	}

	.contain {
		@media (--from-mobile) {
			align-items: start;
			display: grid;
			gap: 2.5rem;
			grid-template-columns: minmax(0, 5fr) minmax(0, 7fr);
			padding-block-end: calc(var(--padding-bottom, var(--s-104)) * 1);
		}
	}

	.content {
		@media (--from-mobile) {
			position: sticky;
			top: 6rem;
		}
	}

	.title {
		color: var(--c-foreground);
		font: 600 var(--f-h2);
		letter-spacing: var(--ls-h2);
		text-wrap: balance;
	}

	.subtitle {
		font: 500 var(--f-h4);
		letter-spacing: var(--ls-h4);
		margin-block-start: 1.5rem;
	}

	.body {
		font: 400 var(--f-body1);
		letter-spacing: var(--ls-body1);
		margin-block-start: 0.75rem;
	}

	@keyframes appear {
		from {
			scale: 0.8;
		}

		to {
			scale: 1;
		}
	}

	.cards {
		display: flex;
		gap: 1rem;

		@media (--until-mobile) {
			margin-inline: -6vw;
			padding: 2.5rem 6vw;
			scroll-padding-inline: 6vw;
			overflow: auto;
			scroll-snap-type: x mandatory;

			&::-webkit-scrollbar {
				display: none;
			}
		}

		@media (--from-mobile) {
			display: grid;
			gap: 2.5rem;
			grid-template-columns: repeat(2, minmax(0, 1fr));
			margin-block-end: 14rem;

			:global {
				.card {
					grid-column: unset;
					view-timeline: --subject-reveal block;
					animation-timeline: --subject-reveal;

					animation-name: appear;
					animation-fill-mode: both;
					animation-duration: 1ms; /* Firefox requires this to apply the animation */

					&:nth-child(even) {
						translate: 0 var(--s-164);
					}
				}
			}
		}

		:global {
			.card {
				flex: 1 0 80%;
				scroll-snap-align: start;
			}
		}
	}

	.tabs {
		display: grid;
		gap: 1rem;
		margin-block-start: 3rem;
	}

	.tab {
		border-block-end: 1px solid var(--c-foreground);
		padding-block-end: 1rem;
		text-align: start;

		&.active {
			color: var(--c-foreground);
		}
	}

	.tab-title {
		font: 600 var(--f-h3);
		letter-spacing: var(--ls-h3);

		&.active {
			text-decoration: underline;
		}
	}

	.tab-subtitle {
		font: 400 var(--f-body1);
		margin-block-start: 0.75rem;
	}
</style>
